import {
  BizError,
  BizResult,
  HttpBizError,
  LoginParam,
  LoginType,
  Ok,
  RemoteDeskInfo,
  User,
} from "@/models/biz_model"
import { RootState } from "@/store"
import {
  ERR_INPUT_VERIFY_CODE_API_REQ,
  ERR_SET_PHONE_API_REQ,
  ERR_SIGNALING_CONNECT_FAILED,
  ERR_SIGNALING_NOT_CONNECTED,
  ERR_VERIFY_CODE_OR_PHONE_FAILED,
} from "@/models/constants"
import { ConnectionUseCase } from "@/usecases/connection"
import { DeviceUseCase } from "@/usecases/device"
import { UserUsecase } from "@/usecases/user"
import { ChangePasswordReminderCache, UserCache } from "@/utils/storage"
import {
  createAsyncThunk,
  createSlice,
  type PayloadAction,
} from "@reduxjs/toolkit"
import { KCSDK } from "@/sdk"
import { DataReportUsecase } from "@/usecases/data_report"
import { isAssistLogin } from "@/pages/login/login_hook"

// import { setAuth } from "@/stores/auth_slice"
import { setAutoLoginPending } from "@/stores/auth_slice"

// 现在改为登录成功后拿到token，传token 并 连接信令服务器
// 登录前检测网络连接状态（支持等待信令恢复）
async function checkConnState(): Promise<BizResult<boolean>> {
  if (!KCSDK.isSignalingConnected()) {
    const ok = await KCSDK.waitSignalingConnected(60000)
    if (!ok) {
      DataReportUsecase.reportError(
        ERR_SIGNALING_CONNECT_FAILED,
        "信令连接失败"
      )
      console.error("UserUseCase signaling not connected")
      return new BizError(ERR_SIGNALING_CONNECT_FAILED)
    }
  }
  return new Ok(true)
}

export enum LoginApiVersion {
  oldApi, // 用来重启/关机云电脑时 校验密码 0
  newApi, // 用来登录，连接云桌面 1
}

export enum ViewType {
  // 账号登录 0
  login,
  // 选择登录过的用户 1
  selectUser,
  // 设置企业登录密码 2
  setKCPwd,
  // 设置手机、验证码 3
  setPhoneVerify,
  // 图片验证码  校验弹窗 4
  imgcodeVerify,
  // 云电脑重启/关机  密码校验弹窗 5
  stopResumeVerify,
  // 个人用户登录 6
  pcLogin,
}

export enum LoginStatus {
  idle, //   空 0
  logging, // 白色文字 1
  error, // 红色文字 2
  success, // 成功状态 3
}

export type StopResumeType = "stop" | "resume"

export interface LoginState {
  // 当前账号输入框中的用户名
  usernameInput: string

  // 当前登录的用户
  currentUser: User | null
  // 所有登录过的用户
  users: User[]
  // 登录提示文案
  msg: string
  // 页面类型
  type: ViewType
  // 登录状态
  status: LoginStatus
  // 图片验证码
  imgCodeObj: { captchaId: string; captcha: string }
  // 重启/关机 云电脑 类型
  stopResumeType: StopResumeType
  // 登录接口版本
  loginApiVersion: LoginApiVersion
  // 是否需要在手机验证码页面，显示图片验证码
  showImgCodeInPhone: boolean

  // 企业登录链接参数
  kcLoginParams: {
    passToken: string
  }
  // 标记是否需要显示90秒处理状态
  shouldShowProcessing: boolean

  // 公钥信息
  publicKeyObj: { public_key: string; version: number }

  // 修改初始密码提示（登录成功后、连接前拦截展示）
  changePasswordPrompt: { message: string; device: RemoteDeskInfo } | null
  // 当前云电脑是否允许客户端重启
  canRestart: boolean
}

const initialState: LoginState = {
  usernameInput: "",
  currentUser: UserCache.getCurrentUser(),
  users: UserCache.getUsers(),
  msg: "",
  status: LoginStatus.idle,
  type: ViewType.login,
  imgCodeObj: { captchaId: "", captcha: "" },
  stopResumeType: "resume",
  loginApiVersion: LoginApiVersion.newApi,
  showImgCodeInPhone: false,
  kcLoginParams: {
    passToken: "",
  },
  shouldShowProcessing: false,
  publicKeyObj: { public_key: "", version: 0 },
  changePasswordPrompt: null,
  canRestart: true,
}

const LoginSlice = createSlice({
  name: "login",
  initialState,
  reducers: {
    setUsernameInput: (state, action: PayloadAction<string>) => {
      state.usernameInput = action.payload
    },
    setImgCodeObj: (
      state,
      action: PayloadAction<{ captchaId: string; captcha: string }>
    ) => {
      state.imgCodeObj = action.payload
    },
    setLoginApiVersion: (state, action: PayloadAction<LoginApiVersion>) => {
      state.loginApiVersion = action.payload
    },
    setStopResumeType: (state, action: PayloadAction<StopResumeType>) => {
      state.stopResumeType = action.payload
    },
    setShowImgCodeInPhone: (state, action: PayloadAction<boolean>) => {
      state.showImgCodeInPhone = action.payload
    },
    setKcLoginParams: (
      state,
      action: PayloadAction<{
        passToken: string
      }>
    ) => {
      state.kcLoginParams = action.payload
    },
    setPublicKeyObj: (
      state,
      action: PayloadAction<{
        public_key: string
        version: number
      }>
    ) => {
      state.publicKeyObj = action.payload
    },

    // 更新登录状态提示信息
    updateLoginStatus: (
      state,
      action: PayloadAction<{
        msg: string | undefined
        status?: LoginStatus
      }>
    ) => {
      const { msg, status } = action.payload
      if (msg !== undefined) {
        state.msg = msg
      }
      if (status) {
        state.status = status
      }
    },
    // 更新当前用户信息
    updateCurrentUser: (state, action: PayloadAction<User>) => {
      const user = action.payload
      state.currentUser = user

      // 保存用户到本地存储
      UserCache.saveUser(user)

      if (user.loginType !== "PC") {
        // 更新登陆过的用户列表
        const existingUserIndex = state.users.findIndex(
          (u) => u.account === user.account
        )
        if (existingUserIndex >= 0) {
          // 用户已存在，更新用户信息
          state.users[existingUserIndex] = user
        } else {
          // 用户不存在，添加新用户
          state.users = [...state.users, user]
        }
      }
    },

    // 切换用户
    changeUser: (state, action: PayloadAction<User>) => {
      const user = action.payload
      state.type = ViewType.login
      state.status = LoginStatus.idle
      state.currentUser = user
      state.msg = ""
      UserCache.setCurrentUser(user)
      // const auth = AuthCache.getAuthHeader(user.account)
      // store.dispatch(setAuth(auth))
    },
    // 跳转到添加新用户
    gotoAddNewUser: (state) => {
      state.type = ViewType.login
      state.status = LoginStatus.idle
      state.currentUser = null
      state.msg = ""
      UserCache.setCurrentUser(null)
      // store.dispatch(setAuth(null))
    },
    // 切换页面类型
    changeViewType: (state, action: PayloadAction<ViewType>) => {
      const previousType = state.type
      state.type = action.payload
      const shouldKeepStopResumeStatus =
        action.payload === ViewType.stopResumeVerify &&
        previousType === ViewType.setPhoneVerify &&
        state.shouldShowProcessing
      // 只有在切换到非登录相关页面时才重置状态
      // 从短信验证码页面返回登录页面时保持当前状态
      //
      // 重置条件：
      // - 如果目标页面不是 login，则重置
      // - 如果目标页面是 login，但上一个页面不是 setPhoneVerify 或 imgcodeVerify，则重置
      // - KS重启/关机手机验证后回到弹窗时，保留真实执行结果
      if (
        !shouldKeepStopResumeStatus &&
        (action.payload !== ViewType.login ||
          (previousType !== ViewType.setPhoneVerify &&
            previousType !== ViewType.imgcodeVerify))
      ) {
        state.status = LoginStatus.idle
        state.msg = ""
      }
    },

    // 删除用户
    deleteUser(state, action: PayloadAction<User>) {
      const user = action.payload
      const { currentUser, users } = state
      const index = users.findIndex((u) => u.account === user.account)
      if (index >= 0) {
        users.splice(index, 1)
        state.users = users
        UserCache.deleteUser(user)
      }
      if (user.account === currentUser?.account) {
        // 删除当前用户
        state.currentUser = null
        UserCache.setCurrentUser(null)
        // store.dispatch(setAuth(null))
      }
    },

    // 设置是否需要显示90秒处理状态
    setShouldShowProcessing: (state, action: PayloadAction<boolean>) => {
      state.shouldShowProcessing = action.payload
    },
    // 设置修改初始密码提示（连接前拦截）
    setChangePasswordPrompt: (
      state,
      action: PayloadAction<{ message: string; device: RemoteDeskInfo } | null>
    ) => {
      state.changePasswordPrompt = action.payload
    },
    // 清除修改初始密码提示
    clearChangePasswordPrompt: (state) => {
      state.changePasswordPrompt = null
    },
    // 设置当前云电脑是否允许客户端重启
    setCanRestart: (state, action: PayloadAction<boolean>) => {
      state.canRestart = action.payload
    },
  },
  extraReducers: (builder) => {
    builder
      // 账户密码或企业登录
      .addCase(userLoginNew.pending, (state) => {
        state.status = LoginStatus.logging
        // 开始账号密码登录
        state.msg = "登录中..."
      })
      .addCase(userLoginNew.fulfilled, (state) => {
        state.status = LoginStatus.success
        // 登录成功
        state.msg = ""
      })
      .addCase(userLoginNew.rejected, (state, action) => {
        // 针对信令未连接的特殊提示
        if (action.payload?.code === ERR_SIGNALING_NOT_CONNECTED) {
          state.status = LoginStatus.logging
          state.msg = "连接服务器中..."
          return
        }
        state.status = LoginStatus.error
        // 登录失败
        state.msg = action.payload?.msg ?? "登录失败"
      })
      .addCase(userAutoLogin.pending, (state) => {
        state.status = LoginStatus.logging
        state.msg = "登录中..."
      })
      .addCase(userAutoLogin.fulfilled, (state) => {
        state.status = LoginStatus.success
        state.msg = ""
      })
      .addCase(userAutoLogin.rejected, (state, action) => {
        state.status = LoginStatus.error
        state.msg = action.payload?.msg ?? "自动登录失败"
      })

      // 重启/关机 云电脑 校验密码
      .addCase(userLoginOld.pending, (state) => {
        state.status = LoginStatus.logging
        // 开始重启/关机 云电脑 校验密码
        // state.msg = "登录中..."
        state.msg = "正在验证用户身份..."
      })
      .addCase(userLoginOld.fulfilled, (state) => {
        state.status = LoginStatus.success
        state.msg = state.stopResumeType === "stop" ? "关机成功" : "重启成功"
      })
      .addCase(userLoginOld.rejected, (state, action) => {
        // 针对信令未连接的特殊提示
        if (action.payload?.code === ERR_SIGNALING_NOT_CONNECTED) {
          state.status = LoginStatus.logging
          // state.msg = "连接服务器中..."
          state.msg = "正在验证用户身份..."
          return
        }
        state.status = LoginStatus.error
        // 校验密码失败
        // state.msg = action.payload?.msg ?? "登录失败"
        state.msg = action.payload?.msg ?? "验证用户身份失败"
      })

      // 个人账户登录
      .addCase(pcLogin.pending, (state) => {
        state.status = LoginStatus.logging
        state.msg = "加载中..."
      })
      .addCase(pcLogin.fulfilled, (state) => {
        state.status = LoginStatus.success
        state.msg = ""
      })
      .addCase(pcLogin.rejected, (state, action) => {
        // 针对信令未连接的特殊提示
        if (action.payload?.code === ERR_SIGNALING_NOT_CONNECTED) {
          state.status = LoginStatus.logging
          state.msg = "连接服务器中..."
          return
        }
        state.status = LoginStatus.error
        state.msg = action.payload?.msg ?? "加载失败"
      })

      // 修改初始密码提示后，继续连接云桌面
      .addCase(resumeConnectRemoteDesk.pending, (state) => {
        state.status = LoginStatus.logging
        state.msg = "连接中..."
      })
      .addCase(resumeConnectRemoteDesk.fulfilled, (state) => {
        state.status = LoginStatus.success
        state.msg = ""
      })
      .addCase(resumeConnectRemoteDesk.rejected, (state, action) => {
        if (action.payload?.code === ERR_SIGNALING_NOT_CONNECTED) {
          state.status = LoginStatus.logging
          state.msg = "连接服务器中..."
          return
        }
        state.status = LoginStatus.error
        state.msg = action.payload?.msg ?? "连接失败"
      })
  },
})

export const {
  setUsernameInput,
  updateLoginStatus,
  changeViewType,
  changeUser,
  gotoAddNewUser,
  updateCurrentUser,
  deleteUser,
  setImgCodeObj,
  setKcLoginParams,
  setPublicKeyObj,
  setStopResumeType,
  setLoginApiVersion,
  setShouldShowProcessing,
  setShowImgCodeInPhone,
  setChangePasswordPrompt,
  clearChangePasswordPrompt,
  setCanRestart,
} = LoginSlice.actions

interface ErrValue {
  code: number
  msg: string
  data?: any
}
function toErrValue(err: BizError | HttpBizError): ErrValue {
  return {
    code: err.code,
    msg: err.displayErrorMsg,
    ...(err instanceof HttpBizError ? { data: err.data } : {}),
  }
}

// 连接云桌面流程（信令→网络检测→开机→agent→连接），供 userLoginNew 与
// resumeConnectRemoteDesk 复用。返回 ErrValue 表示失败，undefined 表示成功。
async function connectRemoteDeskSteps(
  device: RemoteDeskInfo,
  { dispatch }: { dispatch: any }
): Promise<ErrValue | undefined> {
  const connSignalingRes = await ConnectionUseCase.connectToSignalingServer(
    device.id.toString()
  )
  if (connSignalingRes instanceof BizError) {
    return toErrValue(connSignalingRes)
  }

  const checkConnStateRes = await checkConnState()
  if (checkConnStateRes instanceof BizError) {
    return toErrValue(checkConnStateRes)
  }

  if (device.status !== "Running") {
    dispatch(updateLoginStatus({ msg: "开机中..." }))
    const powerOnRes = await DeviceUseCase.ensureDeviceStart(device)
    if (powerOnRes instanceof BizError) {
      return toErrValue(powerOnRes)
    }
  }

  if (device.online !== "online") {
    const agentRes = await DeviceUseCase.ensureAgentOnline(device)
    if (agentRes instanceof BizError) {
      return toErrValue(agentRes)
    }
  }

  dispatch(updateLoginStatus({ msg: "连接中..." }))
  const connRes = await ConnectionUseCase.connectToRemoteDesk(device)
  if (connRes instanceof BizError) {
    return toErrValue(connRes)
  }
  dispatch(updateLoginStatus({ msg: "" }))
  return undefined
}

// 用户登录(新登录接口,用来登录云桌面)
export const userLoginNew = createAsyncThunk<
  undefined,
  LoginParam,
  { rejectValue: ErrValue; state: RootState }
>(
  "login/userLoginNew",
  async (
    { userName, pwd, captchaId, captcha, phone, smsCode, smsToken },
    { getState, dispatch, rejectWithValue }
  ) => {
    const { currentUser } = getState().login
    console.log("Current user:", currentUser, "userName:", userName)

    let user = currentUser
    let account: string
    let ksAuthCode: string
    let loginTag: LoginType

    if (user !== null && user.loginType === "KS") {
      // 企业登录用户
      account = user.account
      ksAuthCode = user.ksAuthCode!
      loginTag = "KS"
    } else {
      // 账号密码登录用户
      account = currentUser?.account ?? userName!
      loginTag = "Account"
    }

    const doLogin = async () =>
      await UserUsecase.newAccountLogin(
        account!,
        pwd,
        loginTag,
        captcha,
        captchaId,
        ksAuthCode,
        phone,
        smsCode,
        smsToken
      )

    const loginRes = await doLogin()
    // 清空图片校验码，图片校验码只能使用一次
    dispatch(setImgCodeObj({ captchaId: "", captcha: "" }))

    if (loginRes instanceof BizError || loginRes instanceof HttpBizError) {
      const err = toErrValue(loginRes)
      return rejectWithValue(err)
    }

    const { userObj, devicesList, needChangePassword, changePasswordMessage } =
      loginRes.unwrap()
    user = userObj

    dispatch(updateCurrentUser(user)) // 更新用户信息
    const device = devicesList[0]
    dispatch(setCanRestart(device.canRestart ?? true))

    // 登录成功后立即建立常驻信令（对齐 android _onLogin reconnectSignaling），
    // 确保未连云桌面（如需改密码提前 return）时也能收到远程协助请求。
    // 失败不阻塞登录主流程；后续连云桌面时会重建信令刷新 token。
    void ConnectionUseCase.connectToSignalingServer(device.id.toString())

    // 登录成功后、连接云桌面前，拦截提示修改初始密码（一天内同账号仅提示一次）
    if (
      needChangePassword
      // &&
      // !ChangePasswordReminderCache.isShownToday(account)
    ) {
      ChangePasswordReminderCache.markShown(account)
      dispatch(
        setChangePasswordPrompt({ message: changePasswordMessage, device })
      )
      return
    }

    const err = await connectRemoteDeskSteps(device, { dispatch })
    if (err) {
      return rejectWithValue(err)
    }
  }
)

/** 使用当前浏览器绑定的设备密钥登录，无需再次输入密码。 */
export const userAutoLogin = createAsyncThunk<
  undefined,
  { phone?: string; smsCode?: string; smsToken?: string } | undefined,
  { rejectValue: ErrValue; state: RootState }
>(
  "login/userAutoLogin",
  async (params, { getState, dispatch, rejectWithValue }) => {
    const { phone, smsCode, smsToken } = params ?? {}
    dispatch(setAutoLoginPending(true))
    const { currentUser } = getState().login
    const fpHash = getState().auth.fpHash
    if (currentUser === null || currentUser.loginType === "PC" || !fpHash) {
      dispatch(setAutoLoginPending(false))
      return rejectWithValue({
        code: -10026,
        msg: "登录状态已过期，请重新登录",
      })
    }

    const loginRes = await UserUsecase.autoLogin(
      currentUser,
      fpHash,
      phone,
      smsCode,
      smsToken
    )
    if (loginRes instanceof BizError || loginRes instanceof HttpBizError) {
      const error = toErrValue(loginRes)
      // 双重验证相关错误(-10008/-10009/-10021)保持 autoLoginPending，
      // 让验证码页继续走自动登录分支（token + deviceSecret），而非退回密码登录。
      if (
        ![
          ERR_SET_PHONE_API_REQ,
          ERR_INPUT_VERIFY_CODE_API_REQ,
          ERR_VERIFY_CODE_OR_PHONE_FAILED,
        ].includes(error.code)
      ) {
        dispatch(setAutoLoginPending(false))
      }
      return rejectWithValue(error)
    }

    const { userObj, devicesList, needChangePassword, changePasswordMessage } =
      loginRes.unwrap()
    dispatch(updateCurrentUser(userObj))
    dispatch(setAutoLoginPending(false))
    const device = devicesList[0]
    dispatch(setCanRestart(device.canRestart ?? true))

    // 登录成功后立即建立常驻信令（对齐 android _onLogin reconnectSignaling），
    // 确保未连云桌面（如需改密码提前 return）时也能收到远程协助请求。
    // 失败不阻塞登录主流程；后续连云桌面时会重建信令刷新 token。
    void ConnectionUseCase.connectToSignalingServer(device.id.toString())

    // 登录成功后、连接云桌面前，拦截提示修改初始密码（一天内同账号仅提示一次）
    if (
      needChangePassword
      // &&
      // !ChangePasswordReminderCache.isShownToday(currentUser.account)
    ) {
      ChangePasswordReminderCache.markShown(currentUser.account)
      dispatch(
        setChangePasswordPrompt({ message: changePasswordMessage, device })
      )
      return
    }

    const connSignalingRes = await ConnectionUseCase.connectToSignalingServer(
      device.id.toString()
    )
    if (connSignalingRes instanceof BizError) {
      dispatch(setAutoLoginPending(false))
      return rejectWithValue(toErrValue(connSignalingRes))
    }
    const checkConnStateRes = await checkConnState()
    if (checkConnStateRes instanceof BizError) {
      dispatch(setAutoLoginPending(false))
      return rejectWithValue(toErrValue(checkConnStateRes))
    }
    if (device.status !== "Running") {
      dispatch(updateLoginStatus({ msg: "开机中..." }))
      const powerOnRes = await DeviceUseCase.ensureDeviceStart(device)
      if (powerOnRes instanceof BizError) {
        dispatch(setAutoLoginPending(false))
        return rejectWithValue(toErrValue(powerOnRes))
      }
    }
    if (device.online !== "online") {
      const agentRes = await DeviceUseCase.ensureAgentOnline(device)
      if (agentRes instanceof BizError) {
        dispatch(setAutoLoginPending(false))
        return rejectWithValue(toErrValue(agentRes))
      }
    }
    dispatch(updateLoginStatus({ msg: "连接中..." }))
    const connRes = await ConnectionUseCase.connectToRemoteDesk(device)
    if (connRes instanceof BizError) {
      dispatch(setAutoLoginPending(false))
      return rejectWithValue(toErrValue(connRes))
    }
    dispatch(updateLoginStatus({ msg: "" }))
  }
)

// 老登录接口,用来重启/关机云电脑时 校验密码
export const userLoginOld = createAsyncThunk<
  undefined,
  LoginParam,
  { rejectValue: ErrValue; state: RootState }
>(
  "login/userLoginOld",
  async (
    { userName, pwd, captchaId, captcha, phone, smsCode, smsToken },
    { getState, dispatch, rejectWithValue }
  ) => {
    const { currentUser, stopResumeType } = getState().login
    let user = currentUser
    let account: string
    let ksAuthCode: string
    let loginTag: LoginType

    if (user !== null && user.loginType === "KS") {
      // 企业登录用户
      account = user.account
      ksAuthCode = user.ksAuthCode!
      loginTag = "KS"
    } else {
      // 账号密码登录用户
      account = currentUser?.account ?? userName!
      loginTag = "Account"
    }

    const doLogin = async () =>
      await UserUsecase.oldAccountLogin(
        account!,
        pwd,
        loginTag,
        captcha,
        captchaId,
        ksAuthCode,
        phone,
        smsCode,
        smsToken
      )

    const loginRes = await doLogin()

    // 清空图片校验码，图片校验码只能使用一次
    dispatch(setImgCodeObj({ captchaId: "", captcha: "" }))

    if (loginRes instanceof BizError || loginRes instanceof HttpBizError) {
      const err = toErrValue(loginRes)
      return rejectWithValue(err)
    }

    const { userObj } = loginRes.unwrap()
    user = userObj
    dispatch(updateCurrentUser(user)) // 更新用户信息

    // 登录成功，由前端 校验弱密码
    // const validationResult: PasswordValidationResult = validatePassword(
    //   pwd,
    //   account
    // )
    // if (!validationResult.isValid) {
    //   dispatch(
    //     updateLoginStatus({
    //       msg: "您当前密码强度较弱，请点击【重置密码】进行修改",
    //       status: LoginStatus.error,
    //     })
    //   )
    //   return
    // }

    // 密码校验通过，执行云电脑关机/重启
    const stopResumeRes = await DeviceUseCase.powerOffOrRestart(
      stopResumeType,
      (msg) => {
        dispatch(updateLoginStatus({ msg, status: LoginStatus.logging }))
      }
    )
    if (stopResumeRes instanceof BizError) {
      return rejectWithValue(toErrValue(stopResumeRes))
    }
  }
)

// 个人账户登录
export const pcLogin = createAsyncThunk<
  undefined,
  { user: User; desktopId: string; deviceId: number },
  { rejectValue: ErrValue; state: RootState }
>(
  "login/pcLogin",
  async ({ user, desktopId, deviceId }, { dispatch, rejectWithValue }) => {
    // 登录成功后，拿到token，传token 并 连接信令服务器
    const connSignalingRes =
      await ConnectionUseCase.connectToSignalingServer(String(deviceId))
    if (connSignalingRes instanceof BizError) {
      return rejectWithValue(toErrValue(connSignalingRes))
    }

    // 检测网络连接状态
    const checkConnStateRes = await checkConnState()
    if (checkConnStateRes instanceof BizError) {
      return rejectWithValue(toErrValue(checkConnStateRes))
    }

    // 更新当前用户
    dispatch(updateCurrentUser(user))
    UserCache.setCurrentUser(user)

    // 获取云桌面设备
    const deviceRes = await DeviceUseCase.getDevice(deviceId, desktopId)
    if (deviceRes instanceof BizError) {
      return rejectWithValue(toErrValue(deviceRes))
    }

    // 开始连接
    const device = deviceRes.unwrap()
    if (device.status !== "Running") {
      // 等待云桌面开机
      dispatch(updateLoginStatus({ msg: "开机中..." }))
      const powerOnRes = await DeviceUseCase.ensureDeviceStart(device)
      if (powerOnRes instanceof BizError) {
        return rejectWithValue(toErrValue(powerOnRes))
      }
    }

    // setTimeout(() => {
    //   dispatch(updateLoginStatus({ msg: "正在努力连接中，请稍后..." }))
    // }, 8000)

    // 查询agent状态
    if (device.online !== "online") {
      const agentRes = await DeviceUseCase.ensureAgentOnline(device)
      if (agentRes instanceof BizError) {
        return rejectWithValue(toErrValue(agentRes))
      }
    }

    UserUsecase.setCurrentUser(user)
    dispatch(updateLoginStatus({ msg: "连接中..." }))
    const connRes = await ConnectionUseCase.connectToRemoteDesk(device, user)

    if (connRes instanceof BizError) {
      return rejectWithValue(toErrValue(connRes))
    }
    if (isAssistLogin) {
      KCSDK.sendMsgBySignaling({
        type: "assist",
        status: "connected",
      })
    }
    dispatch(updateLoginStatus({ msg: "" }))
  }
)

/** 修改初始密码提示弹窗「暂不修改」后，继续连接云桌面。 */
export const resumeConnectRemoteDesk = createAsyncThunk<
  undefined,
  undefined,
  { rejectValue: ErrValue; state: RootState }
>(
  "login/resumeConnectRemoteDesk",
  async (_, { getState, dispatch, rejectWithValue }) => {
    const prompt = getState().login.changePasswordPrompt
    if (!prompt) {
      return rejectWithValue({ code: -1, msg: "无待连接设备" })
    }
    // 立即关闭提示弹窗，再继续连接云桌面
    dispatch(clearChangePasswordPrompt())
    const err = await connectRemoteDeskSteps(prompt.device, { dispatch })
    if (err) {
      return rejectWithValue(err)
    }
  }
)

export const selectLoginState = (state: RootState): LoginState => state.login
export const selectCurrentUser = (state: RootState): User | null =>
  state.login.currentUser
export const selectUsernameInput = (state: RootState): string =>
  state.login.usernameInput
export const selectLoginType = (state: RootState): ViewType => state.login.type
export const selectLoginUsers = (state: RootState): User[] => state.login.users
export const selectStopResumeType = (state: RootState): StopResumeType =>
  state.login.stopResumeType
export const selectImgCodeObj = (state: RootState) => state.login.imgCodeObj
export const selectKcLoginParams = (state: RootState) =>
  state.login.kcLoginParams
export const selectLoginApiVersion = (state: RootState): LoginApiVersion =>
  state.login.loginApiVersion
export const selectShowImgCodeInPhone = (state: RootState): boolean =>
  state.login.showImgCodeInPhone
export const selectPublicKeyObj = (state: RootState) => state.login.publicKeyObj
export const selectChangePasswordPrompt = (state: RootState) =>
  state.login.changePasswordPrompt
export const selectCanRestart = (state: RootState): boolean =>
  state.login.canRestart

export default LoginSlice.reducer
